// "Start from a document": upload a sample, run BDA standard output, review suggested fields (requirement 3.7).
// The same flow is the first step of a new blueprint (requirement 3.7.1, `StartFromDocumentStep`) and a modal from
// the editor header (`SuggestModal`). Both take the optional extraction prompt (requirement 3.7.2), and both let a
// Blueprint_Author preview what BDA extracts with the selected fields (create-doc-interactive Requirement 1).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Checkbox from '@cloudscape-design/components/checkbox';
import Container from '@cloudscape-design/components/container';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import LiveRegion from '@cloudscape-design/components/live-region';
import Modal from '@cloudscape-design/components/modal';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Textarea from '@cloudscape-design/components/textarea';
import {
  canonicalJson,
  MAX_SUGGEST_PROMPT_CHARS,
  mergeForPreview,
  PREVIEW_STALE_SELECTION,
  previewSchemaOfFields,
  SAMPLE_EXPIRED_TEXT,
  selectedSuggestions,
  selectionKey,
  UPLOAD_LIMITS_TEXT,
  type BlueprintFieldNode,
  type ScalarField,
} from '@bda/shared';
import { fieldTypeLabel } from './FieldsEditor';
import { PreviewPanel, STARTING_TEXT, usePreview } from '../preview/PreviewPanel';
import { api, ApiError } from '../../api/client';
import { useRun } from '../../api/hooks';
import { hasRole, useAuth } from '../../auth/AuthProvider';
import { runtimeConfig } from '../../config';
import { errorMessage, MessageWithReference, referenceOf, type Reference } from '../../lib/notify';
import { startDocumentRun } from '../runs/runFlow';
import { useFileCheck } from '../../lib/pageCount';

export const PROMPT_LABEL = 'What do you want to extract?';
export const PROMPT_HINT =
  'For example: "Vendor, invoice total, and every line item with quantity and unit price". Leave it empty for a general set of fields.';
export const TRUNCATED_TEXT =
  'This document is long, so fields were suggested from the beginning of its text only (the first 50,000 characters). Add any fields from later pages yourself.';
export const NO_SELECTION_TEXT = 'Select at least one field.';
export const PAGE_COUNT_UNKNOWN_TEXT = 'Page count unknown. A preview processes every page.';

/** Page text beside "Preview extraction" (Req 1.10). */
export const previewPagesText = (pages: number | null): string =>
  pages === null
    ? PAGE_COUNT_UNKNOWN_TEXT
    : `${formatCount(pages)} ${pages === 1 ? 'page' : 'pages'}. A preview processes every page.`;

export function describeNode(n: BlueprintFieldNode): string {
  if (n.kind === 'field')
    return n.options
      ? `${fieldTypeLabel(n)} (${n.options.join(', ')}): ${n.instruction}`
      : `${fieldTypeLabel(n)}: ${n.instruction}`;
  // A choice inside a group or table shows its allowed values too (task 13.7).
  const child = (f: ScalarField) => (f.options ? `${f.name} (${f.options.join(', ')})` : f.name);
  if (n.kind === 'group') return `Group of ${n.fields.length}: ${n.fields.map(child).join(', ')}`;
  return `Table with columns ${n.columns.map(child).join(', ')}`;
}

function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

const isExpiredError = (e: unknown): boolean =>
  e instanceof ApiError &&
  e.code === 'CONFLICT' &&
  (e.details as { reason?: unknown } | undefined)?.reason === 'DOCUMENT_EXPIRED';

interface SuggestFlow {
  /** Upload, prompt, progress, errors, the suggestion checkboxes, and the preview. */
  body: ReactNode;
  /** "Suggest fields" before suggestions arrive, then "Preview extraction" and "Add N selected". */
  primary: ReactNode;
  suggestions: BlueprintFieldNode[] | null;
  /** True once a Sample_Document is in use (suggestions shown or being suggested again): "Use another document" applies. */
  hasDocument: boolean;
  /** True while an Extraction_Preview is displayed (the modal widens to fit it). */
  previewShown: boolean;
  /** Clears everything, including the prompt. */
  reset: () => void;
  /** Back to the upload and prompt inputs, keeping the prompt (Req 4.2). */
  useAnotherDocument: () => void;
}

/** Accepts the selected suggestions; `sampleRunId` is the Suggest_Run holding the Sample_Document (Req 4.4). */
type OnAccept = (fields: BlueprintFieldNode[], sampleRunId: string) => void;

/**
 * State and controls of one "Start from a document" pass; the caller decides the frame (inline step or modal).
 * `editorFields` is null for the inline first step (the preview uses exactly the selection, Req 1.5) and the Editor's
 * current fields for the modal (the preview uses them plus the new selected fields, Req 1.6).
 * `initialRunId` is an already started Suggest_Run (blueprint-sets Req 5.4, "Create a blueprint from these pages"):
 * the flow starts at "Reading the document" for that run, with no upload, and everything after is unchanged. The run
 * is only read here, never started, so a reload polls the same run.
 */
function useSuggestFlow(
  onAccept: OnAccept,
  idPrefix: string,
  editorFields: BlueprintFieldNode[] | null,
  initialRunId?: string,
): SuggestFlow {
  const [files, setFiles] = useState<File[]>([]);
  const [prompt, setPrompt] = useState('');
  const [progress, setProgress] = useState<number | null>(initialRunId ? 1 : null);
  const [runId, setRunId] = useState<string | undefined>(initialRunId);
  /** The latest run that holds the Sample_Document; kept while suggesting again so a failed retry can be repeated. */
  const [docRunId, setDocRunId] = useState<string | null>(initialRunId ?? null);
  /** "Suggest fields again" for the stored document: no upload input (Req 4.3). */
  const [resuggesting, setResuggesting] = useState(false);
  const [expiredNotice, setExpiredNotice] = useState(false);
  /** The caught failure (its message and, for an API error, its trace ID). */
  const [error, setError] = useState<unknown>(null);
  const [suggestions, setSuggestions] = useState<BlueprintFieldNode[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const run = useRun(runId);
  const runData = run.data && run.data.id === runId ? run.data : undefined;

  const { user } = useAuth();
  const isAuthor = hasRole(user, 'BlueprintAuthor');
  // What a preview of the current selection would be built from (Req 3.5); in the modal also the Editor's fields.
  const basisKey = editorFields ? `${selectionKey(selected)}|${canonicalJson(editorFields)}` : selectionKey(selected);
  const preview = usePreview({ sourceRunId: runId ?? null, enabled: isAuthor, basisKey });

  const runFailed =
    runData?.status === 'FAILED' ? (runData.failureReason ?? 'The document could not be processed.') : null;
  const shownError = error !== null ? errorMessage(error) : runFailed;
  // A failed run shows the trace of the call that finished it, so support can find the BDA job in the logs.
  const shownReference: Reference | undefined =
    error !== null
      ? referenceOf(error)
      : runFailed && runData
        ? {
            traceId: runData.completedTraceId ?? runData.createdTraceId,
            at: runData.finishedAt ?? runData.startedAt,
          }
        : undefined;

  useEffect(() => {
    if (runData?.status === 'SUCCEEDED' && runId && suggestions === null) {
      let current = true;
      api
        .suggest(runId)
        .then((r) => {
          if (!current) return;
          setSuggestions(r.fields);
          setTruncated(r.truncated);
          setSelected(new Set(r.fields.map((_, i) => i)));
        })
        .catch((e: unknown) => current && setError(e));
      return () => {
        current = false;
      };
    }
    return undefined;
  }, [runData?.status, runId, suggestions]);

  const previewFields = (): BlueprintFieldNode[] => {
    const sel = selectedSuggestions(suggestions ?? [], selected);
    return editorFields ? mergeForPreview(editorFields, sel) : sel;
  };
  // Builds the Preview_Schema without touching the Editor or the selection (Req 1.3, 1.7).
  const startPreview = () => preview.request(previewSchemaOfFields(previewFields()), basisKey);

  // Optional automatic preview, once per Suggest_Run, of all suggestions (`preview.autoPreview`, default off).
  const autoPreviewed = useRef<string | null>(null);
  const autoPreview = runtimeConfig().preview.autoPreview;
  useEffect(() => {
    if (!autoPreview || !isAuthor || !runId || !suggestions || suggestions.length === 0 || selected.size === 0) return;
    if (autoPreviewed.current === runId) return;
    autoPreviewed.current = runId;
    startPreview();
    // startPreview reads the current suggestions and selection; the ref keeps this to one request per run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPreview, isAuthor, runId, suggestions, selected]);

  const clearReview = () => {
    setProgress(null);
    setRunId(undefined);
    setError(null);
    setSuggestions(null);
    setTruncated(false);
    setSelected(new Set());
    preview.clear();
  };
  const anotherDocument = () => {
    clearReview();
    setFiles([]);
    setDocRunId(null);
    setResuggesting(false);
    setExpiredNotice(false);
  };
  const reset = () => {
    anotherDocument();
    setPrompt('');
  };

  const { error: fileError, checking } = useFileCheck(files[0]);
  const promptTooLong = prompt.trim().length > MAX_SUGGEST_PROMPT_CHARS;

  const start = async () => {
    setError(null);
    setExpiredNotice(false);
    setResuggesting(false);
    try {
      setProgress(0);
      const trimmed = prompt.trim();
      const id = await startDocumentRun({
        file: files[0]!,
        mode: 'suggest',
        prompt: trimmed || undefined,
        onProgress: setProgress,
      });
      setRunId(id);
      setDocRunId(id);
    } catch (e) {
      setError(e);
      setProgress(null);
    }
  };

  // Same document, new (or no) prompt, no upload (Req 4.3). Earlier suggestions and previews are discarded at once.
  const suggestAgain = async () => {
    const source = docRunId;
    if (!source) return;
    clearReview();
    setResuggesting(true);
    setProgress(1);
    try {
      const next = await api.retryRun(source, { prompt: prompt.trim() });
      setRunId(next.id);
      setDocRunId(next.id);
    } catch (e) {
      setProgress(null);
      if (isExpiredError(e)) {
        // The stored document is gone: back to the upload input with the prompt kept (Req 4.11).
        anotherDocument();
        setExpiredNotice(true);
      } else {
        setError(e);
      }
    }
  };

  const working = progress !== null && !suggestions && !shownError;
  // Reading the run handed in as `initialRunId`: there is no file to show, so no upload input either (a failed run
  // shows it again, so another document can be chosen).
  const readingSample = working && initialRunId !== undefined && runId === initialRunId;
  const docExpired = runData?.expired === true;
  const previewExpired = preview.state.attempt?.phase === 'rejected' && preview.state.attempt.error.kind === 'expired';

  const disabledReason = !files[0]
    ? 'Choose a sample document first.'
    : fileError
      ? 'Choose a supported document first.'
      : promptTooLong
        ? `Shorten the prompt to ${formatCount(MAX_SUGGEST_PROMPT_CHARS)} characters.`
        : checking
          ? 'Checking the document.'
          : working
            ? 'Suggesting fields.'
            : undefined;
  const againReason = promptTooLong
    ? `Shorten the prompt to ${formatCount(MAX_SUGGEST_PROMPT_CHARS)} characters.`
    : docExpired
      ? SAMPLE_EXPIRED_TEXT
      : working
        ? 'Suggesting fields.'
        : undefined;
  const previewReason =
    selected.size === 0
      ? NO_SELECTION_TEXT
      : preview.status.busy
        ? STARTING_TEXT
        : docExpired || previewExpired
          ? SAMPLE_EXPIRED_TEXT
          : undefined;

  const suggestAgainButton = (variant: 'primary' | 'normal') => (
    <Button
      variant={variant}
      disabled={againReason !== undefined}
      disabledReason={againReason}
      loading={working}
      onClick={() => void suggestAgain()}
    >
      Suggest fields again
    </Button>
  );

  let primary: ReactNode;
  if (suggestions && runId) {
    primary = (
      <SpaceBetween direction="horizontal" size="xs">
        {isAuthor && suggestions.length > 0 && (
          <Box color="text-body-secondary" padding={{ top: 'xxs' }}>
            <span id={`${idPrefix}-preview-pages`}>
              {previewPagesText(runData?.sourcePages ?? runData?.pages ?? null)}
            </span>
          </Box>
        )}
        {isAuthor && suggestions.length > 0 && (
          <Button
            disabled={previewReason !== undefined}
            disabledReason={previewReason}
            ariaDescribedby={`${idPrefix}-preview-pages`}
            onClick={startPreview}
          >
            Preview extraction
          </Button>
        )}
        <Button
          variant="primary"
          disabled={selected.size === 0}
          disabledReason={selected.size === 0 ? NO_SELECTION_TEXT : undefined}
          onClick={() => {
            // The fields the reducer merges are exactly those a modal preview used (mergeForPreview, Req 1.9).
            onAccept(selectedSuggestions(suggestions, selected), runId);
            reset();
          }}
        >
          Add {selected.size} selected
        </Button>
      </SpaceBetween>
    );
  } else if (resuggesting) {
    primary = suggestAgainButton('primary');
  } else {
    primary = (
      <Button
        variant="primary"
        disabled={disabledReason !== undefined}
        disabledReason={disabledReason}
        loading={working}
        onClick={() => void start()}
      >
        Suggest fields
      </Button>
    );
  }

  const body = (
    <SpaceBetween size="m">
      {shownError && (
        <Alert type="error" header="Could not suggest fields">
          <MessageWithReference message={shownError} reference={shownReference} action="Suggest fields" />
        </Alert>
      )}
      {(expiredNotice || docExpired) && <Alert type="warning">{SAMPLE_EXPIRED_TEXT}</Alert>}
      {!suggestions && !resuggesting && !readingSample && (
        <FormField label="Sample document" description={UPLOAD_LIMITS_TEXT} errorText={fileError}>
          <FileUpload
            value={files}
            onChange={(e) => setFiles(e.detail.value)}
            accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
            i18nStrings={{
              uploadButtonText: () => 'Choose file',
              dropzoneText: () => 'Drop a file to upload',
              removeFileAriaLabel: () => 'Remove file',
              limitShowFewer: 'Show fewer',
              limitShowMore: 'Show more',
              errorIconAriaLabel: 'Error',
              warningIconAriaLabel: 'Warning',
            }}
            showFileSize
          />
        </FormField>
      )}
      {/* The prompt stays editable after suggestions arrive, so the author can suggest again (Req 4.3). */}
      <FormField
        label={
          <span>
            {PROMPT_LABEL} <i>- optional</i>
          </span>
        }
        description={PROMPT_HINT}
        constraintText={`${formatCount(prompt.trim().length)}/${formatCount(MAX_SUGGEST_PROMPT_CHARS)} characters`}
        errorText={
          promptTooLong ? `Keep the prompt to ${formatCount(MAX_SUGGEST_PROMPT_CHARS)} characters.` : undefined
        }
        secondaryControl={suggestions ? suggestAgainButton('normal') : undefined}
      >
        <Textarea
          value={prompt}
          rows={3}
          disabled={working}
          controlId={`${idPrefix}-prompt`}
          onChange={(e) => setPrompt(e.detail.value)}
        />
      </FormField>
      {working && progress !== null && progress < 1 && (
        <ProgressBar value={Math.round(progress * 100)} label="Uploading" />
      )}
      {working && (progress ?? 0) >= 1 && (
        <StatusIndicator type="loading">
          Reading the document. This usually takes under a minute (longer documents take a few minutes).
        </StatusIndicator>
      )}
      <LiveRegion hidden>
        {suggestions
          ? suggestions.length === 0
            ? 'No fields could be suggested.'
            : `${suggestions.length} fields suggested.`
          : ''}
      </LiveRegion>
      {suggestions && truncated && (
        <Alert type="info" header="Only the beginning of the document was used">
          {TRUNCATED_TEXT}
        </Alert>
      )}
      {suggestions && suggestions.length === 0 && <Box>No fields could be suggested for this document.</Box>}
      {/* The checkboxes stay enabled while a preview runs or is shown (Req 2.2, 3.4). */}
      {suggestions?.map((s, i) => (
        <Checkbox
          key={i}
          checked={selected.has(i)}
          onChange={(e) => {
            const next = new Set(selected);
            if (e.detail.checked) next.add(i);
            else next.delete(i);
            setSelected(next);
          }}
          description={describeNode(s)}
        >
          {s.name}
        </Checkbox>
      ))}
      {suggestions && suggestions.length > 0 && (
        <Box color="text-body-secondary">You can edit or remove any field after adding it.</Box>
      )}
      {suggestions && runId && (
        <PreviewPanel preview={preview} basisKey={basisKey} staleText={PREVIEW_STALE_SELECTION} idPrefix={idPrefix} />
      )}
    </SpaceBetween>
  );
  return {
    body,
    primary,
    suggestions,
    hasDocument: suggestions !== null || resuggesting,
    previewShown: preview.state.shown !== null,
    reset,
    useAnotherDocument: anotherDocument,
  };
}

/**
 * From the editor header: add more suggestions to the blueprint being edited. `editorFields` are the Editor's current
 * fields, so a preview shows what the blueprint would extract after "Add N selected" (Req 1.6).
 */
export function SuggestModal({
  visible,
  onDismiss,
  onAccept,
  editorFields = [],
}: {
  visible: boolean;
  onDismiss: () => void;
  onAccept: OnAccept;
  editorFields?: BlueprintFieldNode[];
}) {
  const flow = useSuggestFlow(
    (fields, sampleRunId) => {
      onAccept(fields, sampleRunId);
      onDismiss();
    },
    'suggest-modal',
    editorFields,
  );
  const close = () => {
    flow.reset();
    onDismiss();
  };
  return (
    <Modal
      visible={visible}
      onDismiss={close}
      size={flow.previewShown ? 'max' : 'large'}
      header="Start from a document"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close}>
              Cancel
            </Button>
            {flow.hasDocument && (
              <Button variant="link" onClick={flow.useAnotherDocument}>
                Use another document
              </Button>
            )}
            {flow.primary}
          </SpaceBetween>
        </Box>
      }
    >
      {flow.body}
    </Modal>
  );
}

/**
 * First step of a new blueprint (requirement 3.7.1): start from a sample document, or start blank. Inline on the
 * page rather than an auto-opened modal, so it reads in order (upload, prompt, Suggest fields, then the blank
 * option) and needs no focus trap. `initialRunId` starts the flow at "Reading the document" for an already started
 * Suggest_Run (blueprint-sets Req 5.4).
 */
export function StartFromDocumentStep({
  onAccept,
  onBlank,
  initialRunId,
}: {
  onAccept: OnAccept;
  onBlank: () => void;
  initialRunId?: string;
}) {
  const flow = useSuggestFlow(onAccept, 'start-step', null, initialRunId);
  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="Upload a sample document. BDA reads it and suggests fields you can accept, edit, or discard."
          >
            Start from a document
          </Header>
        }
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            {flow.hasDocument && (
              <Button variant="link" onClick={flow.useAnotherDocument}>
                Use another document
              </Button>
            )}
            {flow.primary}
          </SpaceBetween>
        }
      >
        {flow.body}
      </Container>
      <Container
        header={
          <Header variant="h2" description="Define every field yourself in the form.">
            Or start with a blank blueprint
          </Header>
        }
      >
        <Button onClick={onBlank}>Start with a blank blueprint</Button>
      </Container>
    </SpaceBetween>
  );
}
